import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor to include auth token
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

export interface CreateUserResponse {
  accountName: string;
  accountPublic: string;
}

export interface TransferRequest {
  vin: {
    from: string;
    to: string;
  };
  vout?: {
    from: string;
    to: string;
  };
  transaction_index: string;
  quantity: number;
  memo: string;
  callback: string;
}

export interface TransferResponse {
  message: string;
}

export interface Wallet {
  id: string;
  publicKey: string;
  type: string;
  balance: string;
  solBalance: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Transaction {
  id: string;
  transactionId: string;
  type: string;
  status: string;
  amount: string;
  signature: string;
  errorMessage: string | null;
  fromWallet: Wallet | null;
  toWallet: Wallet | null;
  memo: any;
  metadata: any;
  createdAt: string;
  updatedAt: string;
}

export const blockchainService = {
  // Create a new blockchain account
  createUser: async (): Promise<CreateUserResponse> => {
    const response = await api.get('/blockchain/create');
    return response.data;
  },

  // Transfer tokens
  transfer: async (transferData: TransferRequest): Promise<TransferResponse> => {
    const response = await api.post('/blockchain/transfer', transferData);
    return response.data;
  },

  // Get all wallets (fetch more for dropdowns)
  getWallets: async (): Promise<Wallet[]> => {
    const response = await api.get('/wallet?page=1&limit=100');
    // Handle paginated response
    return response.data.data || response.data;
  },

  // Get all transactions
  getTransactions: async (): Promise<Transaction[]> => {
    const response = await api.get('/transaction?page=1&limit=100');
    // Handle paginated response
    return response.data.data || response.data;
  },

  // Get failed transactions
  getFailedTransactions: async (): Promise<Transaction[]> => {
    const response = await api.get('/transaction/filter/search?status=failed&page=1&limit=100');
    // The filter endpoint returns a paginated `{ data, meta }` shape.
    return response.data.data || response.data;
  },

  // Retry a failed transaction
  retryTransaction: async (transactionId: string): Promise<{ message: string }> => {
    const response = await api.post(`/transaction/${transactionId}/retry`);
    return response.data;
  },

  // Update wallet balance
  updateWalletBalance: async (walletId: string): Promise<{ message: string; newBalance: number }> => {
    const response = await api.post(`/wallet/${walletId}/update-balance`);
    return response.data;
  },

  // Get wallet details
  getWalletDetails: async (walletId: string): Promise<Wallet & { recentTransactions: Transaction[] }> => {
    const response = await api.get(`/wallet/${walletId}`);
    return response.data;
  },

  // Get transaction details
  getTransactionDetails: async (transactionId: string): Promise<Transaction> => {
    const response = await api.get(`/transaction/${transactionId}`);
    return response.data;
  },
};
